import React from "react";
import CaseStudy from "../case/CaseStudy";
import {
  Section,
  Figure,
  Split,
  Devices,
  Stage,
  Specs,
  Beat,
  Annotated,
} from "../case/blocks";
import { useStageParallax } from "../case/motion";

import AmazonLogo from "../../../images/hive/amazon-logo.png";
import imgIntro from "../../../images/hive/fig-intro.png";
import imgIdeation from "../../../images/hive/fig-ideation.png";
import imgScenario from "../../../images/hive/fig-scenario.png";
import imgTechnicalRender from "../../../images/hive/fig-technical-render.png";
import imgExplodedRender from "../../../images/hive/fig-exploded-render.png";
import imgSensingRender from "../../../images/hive/fig-sensing-render.png";
import imgDevMonitor from "../../../images/hive/hive-devices-monitor.webp";
import imgDevPhoneBack from "../../../images/hive/hive-devices-phone-back.webp";
import imgDevPhoneFront from "../../../images/hive/hive-devices-phone-front.webp";
import imgPackaging from "../../../images/hive/fig-packaging.png";
import imgInstall1 from "../../../images/hive/fig-install-1.png";
import imgInstall2 from "../../../images/hive/fig-install-2.png";
import imgInstall3 from "../../../images/hive/fig-install-3.png";
import imgInstall4 from "../../../images/hive/fig-install-4.png";
import imgInstall5 from "../../../images/hive/fig-install-5.png";
import imgHero from "../../../images/hive/fig-hero.png";

const AMAZON_URL =
  "https://www.amazon.in/HIVE-Advanced-Allergens-Real-Time-App-Controlled/dp/B0FJ5R8JN7";

// The exploded view is reconstructed in its original 1920x1080 artboard frame.
// Dots sit on the outer end of each leader line and the labels run outward
// from there, so every part is pointed at rather than merely listed.
// Coordinates are the ones the original layout used.
const EXPLODED_FRAME = { w: 1920, h: 1080 };
const EXPLODED_MEDIA = { l: 330.5, t: 146, w: 1259, h: 840 };

const pct = (x, y) => ({
  x: (x / EXPLODED_FRAME.w) * 100,
  y: (y / EXPLODED_FRAME.h) * 100,
});

const EXPLODED_PARTS = [
  { label: "Inlet cover", ...pct(1508, 431), side: "right" },
  { label: "Hepa filter", ...pct(367, 455), side: "left" },
  { label: "Activated Carbon filter", ...pct(367, 533), side: "left" },
  { label: "Sensors", ...pct(1508, 580), side: "right" },
  { label: "Antenna", ...pct(1506.5, 641.5), side: "right" },
  { label: "On/Off button", ...pct(366.5, 645.5), side: "left" },
  { label: "Centrifugal fans", ...pct(1508, 730), side: "right" },
  { label: "Outlet cover", ...pct(368, 808), side: "left" },
];

// Leader lines in artboard coordinates, in the same order as the parts above.
// Centrifugal fans had no rule in the original drawing and keeps none here.
const EXPLODED_LINES = [
  [1361, 431, 1508, 431],
  [367, 455, 769, 528],
  [367, 533, 720, 573],
  [1342, 580, 1508, 580],
  [1177, 678.5, 1506.5, 641.5],
  [366.5, 645.5, 598, 645.5],
  [368, 808, 588, 808],
];

const INSTALLS = [
  { src: imgInstall1, alt: "HIVE wall unit above an office meeting room" },
  { src: imgInstall2, alt: "HIVE wall unit beside interior planting" },
  { src: imgInstall3, alt: "HIVE floor unit at a climbing gym" },
  { src: imgInstall4, alt: "HIVE floor unit in a living room beside a fireplace" },
  { src: imgInstall5, alt: "HIVE floor unit beside a sofa in a residential interior" },
];

const SENSORS = [
  "Temperature and humidity sensor",
  "Particulate matter sensor",
  "CO2 sensor",
  "Ambient sound sensor",
  "Volatile organic compounds sensor",
];

const SPECS = [
  {
    label: "Ambient Environment Sensing",
    body: "Removes PM 1.0, PM 2.5, PM 10, VOCs, and Microbes from the air.",
  },
  { label: "Best in-class clean air delivery rate of 5ACH" },
  { label: "All-metal Activated Carbon Filtration" },
  { label: "High Efficiency Filtration with H-14 (99.99%) removal" },
];

const HIVE = () => {
  useStageParallax();

  return (
    <CaseStudy
      slug="/hive"
      title="HIVE"
      tagline="Professional Project"
      year="2023 & 2025"
      client={{ name: "Praan", href: "https://www.praan.io" }}
      roles={[
        "Conceptualisation & sketching",
        "User experience and UI design",
        "3D modeling & visualisation",
        "Concept to product development",
      ]}
      hero={{ src: imgIntro, alt: "HIVE air purifier, front three-quarter view" }}
      links={[{ label: "Available on Amazon", href: AMAZON_URL, logo: AmazonLogo }]}
      lead={
        <p>
          HIVE is an indoor air purifier and mobile application built for both
          B2B and B2C audiences. During my time at Praan Inc,{" "}
          <strong>
            I led the design of the purifier and designed the UI and user
            experience of the app.
          </strong>
        </p>
      }
    >
      <Section id="ideation" title="Ideation">
        <Figure
          src={imgIdeation}
          alt="Ideation board covering technology, form iterations and design of the HEPA filter"
          caption="Ideation board: technology, form and filter design"
        />
      </Section>

      <Section
        id="scenario"
        title="Scenario"
        lead="Hive was proposed to be built for indoor environments mainly offices, malls and schools etc."
      >
        <Figure
          src={imgScenario}
          alt="Isometric cutaways of offices, hotels, gyms, schools and retail interiors where HIVE is installed"
        />
      </Section>

      <Section id="spec">
        <Split
          media={
            <img
              src={imgTechnicalRender}
              alt="HIVE technical render showing the filtration stack"
            />
          }
          align="center"
        >
          <Beat title="Technical Specification">
            <p>
              Removes PM 1.0, PM 2.5, PM 10, VOCs, and Microbes from the air.
            </p>
          </Beat>
          <Specs items={SPECS} columns={1} />
        </Split>
      </Section>

      <Section
        id="exploded"
        title="Exploded view"
        lead="The entire body of the HIVE device is made up of aluminium."
      >
        <Annotated
          src={imgExplodedRender}
          alt="Exploded view of the HIVE purifier showing every internal component"
          points={EXPLODED_PARTS}
          lines={EXPLODED_LINES}
          frame={EXPLODED_FRAME}
          media={EXPLODED_MEDIA}
        />
      </Section>

      <Section
        id="sensing"
        title="Continuous Environmental Sensing"
        lead="Hive included technology that would give the user immediate feedback of not only air quality but much more."
      >
        <Split
          media={
            <img
              src={imgSensingRender}
              alt="Cutaway render of the HIVE sensing module reading the air around it"
            />
          }
          align="center"
        >
          <Specs items={SENSORS} columns={1} />
        </Split>
      </Section>

      <Devices
        monitor={{
          src: imgDevMonitor,
          alt: "HIVE building dashboard listing rooms, devices and air quality",
        }}
        phones={[
          {
            src: imgDevPhoneBack,
            alt: "HIVE mobile app splash screen",
          },
          {
            src: imgDevPhoneFront,
            alt: "HIVE mobile app showing bedroom air quality readings",
          },
        ]}
      >
        <Beat title="Infinite Scaling with Centralised Control">
          <p>
            HIVE and its platforms scale as per the infrastructure without
            limitations.
          </p>
        </Beat>
        <Beat title="Feel, See, and Measure the impact">
          <p>
            The HIVE app tracks every particle you breathe, learns your
            environment, sends smart alerts, schedules purification
            automatically, and keeps you informed with live filter and
            performance update.
          </p>
        </Beat>
      </Devices>

      <Section id="packaging" title="Packaging">
        <Split
          media={
            <img
              src={imgPackaging}
              alt="HIVE packaging iterations in moulded pulp and corn starch foam"
            />
          }
          reverse
        >
          <p className="cs-lead" style={{ marginTop: 0 }}>
            There were multiple Iterations made for the packaging of HIVE as I
            wanted to create a minimal, clean and sustainable packaging.
          </p>
          <Beat title="Green Cell Foam">
            <p>
              Highly sustainable packaging option. Green cell foam is made out
              of corn, which makes it highly compostable.
            </p>
          </Beat>
          <Beat title="Corn starch packaging">
            <p>
              Its material is developed using polylactic acid (PLA), made by
              fermenting sugars of the plant.
            </p>
          </Beat>
          <Beat title="Molded Pulp">
            <p>
              Its material is developed using polylactic acid (PLA), made by
              fermenting sugars of the plant.
            </p>
          </Beat>
        </Split>
      </Section>

      <Section
        id="installed"
        title="Hive installed in real environments"
        lead="Following are images of the HIVE devices installed in multiple offices in Mumbai, India."
      >
        <div className="cs-band" data-reveal>
          {INSTALLS.map((im) => (
            <img
              key={im.src}
              src={im.src}
              alt={im.alt}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      </Section>

      <Stage
        src={imgHero}
        alt="Production ready HIVE purifier"
        title="Discover the production ready version."
        height="100dvh"
      >
        <a
          className="cs-link"
          href={AMAZON_URL}
          target="_blank"
          rel="noreferrer"
          style={{ marginTop: "1.5rem" }}
        >
          <img src={AmazonLogo} alt="Buy HIVE on Amazon" />
        </a>
      </Stage>
    </CaseStudy>
  );
};

export default HIVE;
